import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { NZ_REGIONS } from '../lib/marketplace-categories';
import { saveAuth, isLoggedIn } from '../lib/marketplace-auth';

type Mode = 'signin' | 'register';

export default function MarketplaceSignIn() {
  const [mode, setMode] = useState<Mode>('signin');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    region: '',
    password: '',
    confirm: '',
  });

  useEffect(() => {
    if (isLoggedIn()) {
      navigate('/marketplace/dashboard', { replace: true });
    }
  }, [navigate]);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (mode === 'register') {
      if (form.password.length < 8) return setError('Password must be at least 8 characters.');
      if (form.password !== form.confirm) return setError('Passwords do not match.');
    }

    setLoading(true);
    try {
      const endpoint = mode === 'signin' ? 'auth-marketplace-login' : 'auth-register';
      const payload =
        mode === 'signin'
          ? { email: form.email, password: form.password }
          : {
              name: form.name,
              email: form.email,
              phone: form.phone || undefined,
              region: form.region || undefined,
              password: form.password,
            };

      const res = await fetch(`/.netlify/functions/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong');
        return;
      }
      saveAuth(data.token, data.user);
      navigate('/marketplace/dashboard');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const labelCls = 'block text-xs font-semibold text-text-secondary mb-2';

  return (
    <div className="max-w-[440px] mx-auto px-4 md:px-8 py-6 md:py-12 pb-24">
      <div className="flex items-center gap-2 text-sm text-text-muted mb-4">
        <Link to="/marketplace" className="text-text-secondary hover:text-accent transition-colors">Marketplace</Link>
        <span>/</span>
        <span>{mode === 'signin' ? 'Sign in' : 'Create account'}</span>
      </div>

      <h1 className="font-display text-2xl md:text-[1.8rem] font-bold mb-2">
        {mode === 'signin' ? 'Marketplace sign in' : 'Create a marketplace account'}
      </h1>
      <p className="text-text-secondary mb-6 text-sm">
        {mode === 'signin'
          ? 'Sign in to manage your for-sale listings.'
          : 'Free account - list boats, gear and equipment for sale.'}
      </p>

      {/* Mode toggle */}
      <div className="flex bg-surface-light dark:bg-surface rounded-lg p-1 mb-6 border border-[rgba(16,42,66,0.12)]">
        {(['signin', 'register'] as Mode[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => { setMode(m); setError(''); }}
            className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
              mode === m
                ? 'bg-accent text-white'
                : 'text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary'
            }`}
          >
            {m === 'signin' ? 'Sign in' : 'Create account'}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {mode === 'register' && (
          <div>
            <label className={labelCls}>Full name</label>
            <input
              className="form-input"
              type="text"
              required
              value={form.name}
              onChange={set('name')}
              placeholder="Your name"
              autoComplete="name"
            />
          </div>
        )}
        <div>
          <label className={labelCls}>Email</label>
          <input
            className="form-input"
            type="email"
            required
            value={form.email}
            onChange={set('email')}
            placeholder="you@example.co.nz"
            autoComplete="email"
          />
        </div>
        {mode === 'register' && (
          <>
            <div>
              <label className={labelCls}>Phone (optional)</label>
              <input
                className="form-input"
                type="tel"
                value={form.phone}
                onChange={set('phone')}
                placeholder="021 000 000"
                autoComplete="tel"
              />
            </div>
            <div>
              <label className={labelCls}>Region</label>
              <select className="form-input" value={form.region} onChange={set('region')}>
                <option value="">Choose a region…</option>
                {NZ_REGIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </>
        )}
        <div>
          <label className={labelCls}>Password</label>
          <input
            className="form-input"
            type="password"
            required
            value={form.password}
            onChange={set('password')}
            placeholder={mode === 'register' ? 'At least 8 characters' : '••••••••'}
            autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
          />
        </div>
        {mode === 'register' && (
          <div>
            <label className={labelCls}>Confirm password</label>
            <input
              className="form-input"
              type="password"
              required
              value={form.confirm}
              onChange={set('confirm')}
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </div>
        )}
        <button type="submit" disabled={loading} className="btn btn-primary btn-lg btn-block">
          {loading ? 'Please wait…' : mode === 'signin' ? 'Sign in' : 'Create account'}
        </button>
      </form>

      <div className="mt-8 bg-card-light dark:bg-card border border-[rgba(79,179,217,0.14)] rounded-lg p-4">
        <p className="text-text-secondary text-xs">
          This is a marketplace account for buying &amp; selling gear. Business owners managing a directory listing should use the{' '}
          <Link to="/signin" className="text-accent hover:underline">business sign in</Link> instead.
        </p>
      </div>
    </div>
  );
}
